import { THEME } from '../../../../web/components-v2/Theme/theme';

/** Shared by every pill in the thread author row; `flex: none` keeps it from being squeezed on phones,
 * where that row is a nowrap flex line carrying the timestamp too. */
const threadHeaderBadge = {
	display: 'inline-block',
	flex: 'none',
	fontSize: THEME.typography.fontSizes.xxs,
	padding: '2px 8px',
	borderRadius: 3,
	fontWeight: THEME.typography.fontWeights.semibold,
} as const;

export const threadTimelineStyles = {
	// Spacing sits above each item, not below, so an email always has the full gap before it while an
	// activity row stays tucked under the item it follows.
	thread: {
		marginTop: THEME.space[24],
		':first-child': { marginTop: 0 },
		padding: '12px 18px',
		borderRadius: THEME.common.borderRadius,
		border: `1px solid ${THEME.colors.border.main}`,
	},
	threadCustomer: {
		backgroundColor: THEME.colors.background.light,
	},
	threadAgent: {
		backgroundColor: THEME.colors.primary.light,
		borderColor: THEME.colors.border.main,
	},
	threadNote: {
		backgroundColor: THEME.colors.info.light,
		borderColor: THEME.colors.info.main,
	},
	threadAiDraft: {
		backgroundColor: THEME.colors.secondary.light,
		borderColor: THEME.colors.secondary.main,
	},
	// A forward is an agent-authored email, so it keeps the agent blue; the accent bar marks it as
	// out-of-chain internal traffic rather than part of the customer conversation.
	threadForward: {
		backgroundColor: THEME.colors.primary.light,
		borderColor: THEME.colors.border.main,
		borderLeft: `3px solid ${THEME.colors.tertiary.main}`,
	},
	threadSystem: {
		backgroundColor: THEME.colors.background.main,
		borderColor: THEME.colors.border.main,
		// Activity rows (category change, AI draft, etc.) are compact metadata, not
		// messages — tighten their padding and gap so a run of them doesn't sprawl.
		marginTop: THEME.space[8],
		padding: '6px 16px',
		textAlign: 'center' as const,
		fontSize: 13,
		color: THEME.colors.text.light,
	},
	/** Emphasis for the actor name within an activity event row. */
	eventActor: { fontWeight: THEME.typography.fontWeights.bold, color: THEME.colors.text.main },
	/** Muted timestamp suffix on an activity event row. */
	eventTime: { color: THEME.colors.text.light },
	threadRejected: { opacity: 0.5 },
	threadAvatarAiDraft: { backgroundColor: THEME.colors.secondary.dark, color: THEME.colors.secondary.contrastText },
	draftActions: {
		display: 'flex',
		gap: 8,
		marginTop: 12,
		paddingTop: 12,
		borderTop: `1px solid ${THEME.colors.border.main}`,
	},
	draftAccepted: {
		color: THEME.colors.success.dark,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	draftRejectedLabel: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	collapsedToggle: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.secondary.dark,
		cursor: 'pointer',
		padding: '4px 0',
		':hover': { textDecoration: 'underline' },
	},
	threadHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		gap: THEME.spacing.sm,
		minWidth: 0,
		marginBottom: 8,
	},
	threadAuthorRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		flex: '1 1 auto',
		minWidth: 0,
		// Phone stacks name/stamp over the address, so the avatar belongs beside the first line.
		'@media (max-width: 767px)': { alignItems: 'flex-start' },
	},
	// Long sender addresses would otherwise widen the card past the phone viewport.
	threadAuthorMeta: {
		minWidth: 0,
	},
	threadAvatar: {
		flex: 'none',
		width: 32,
		height: 32,
		borderRadius: '50%',
		backgroundColor: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	threadAvatarCustomer: {
		backgroundColor: THEME.colors.border.main,
		color: THEME.colors.text.light,
	},
	threadAvatarNote: {
		backgroundColor: THEME.colors.warning.main,
		color: THEME.colors.warning.contrastText,
	},
	threadAuthorName: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		// Phone carries the timestamp on this row (see threadTimeInline), so it becomes a flex row.
		// It must not wrap: a stranded, right-aligned stamp on its own line was the whole complaint.
		'@media (max-width: 767px)': {
			display: 'flex',
			flexWrap: 'nowrap',
			alignItems: 'baseline',
			gap: THEME.spacing.xs + 2,
		},
	},
	// The name is what gives way when the row runs out of room, not the stamp.
	threadAuthorNameText: {
		'@media (max-width: 767px)': {
			minWidth: 0,
			...THEME.common.truncate,
		},
	},
	// Keeps the stamp beside the name instead of in a column of its own, which squeezed the address
	// lines below it into mid-word breaks.
	threadTimeInline: {
		flex: 'none',
		marginLeft: 'auto',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.light,
		whiteSpace: 'nowrap' as const,
	},
	threadAuthorEmail: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		overflowWrap: 'break-word' as const,
	},
	threadRecipients: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		overflowWrap: 'break-word' as const,
	},
	/** Timestamp and note actions, centered on one line at the header's top right. */
	threadHeaderMeta: {
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		flex: 'none',
	},
	noteActionTrigger: {
		flex: 'none',
		// Matches NoteDeleteAction: the 24px hit area would otherwise make the header taller than the timestamp line.
		marginTop: -4,
		marginBottom: -4,
		color: THEME.colors.text.light,
		':hover': {
			color: THEME.colors.primary.main,
		},
	},
	threadTime: {
		flex: 'none',
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		whiteSpace: 'nowrap' as const,
		textAlign: 'right' as const,
	},
	threadBody: {
		fontSize: THEME.typography.fontSizes.sm,
		lineHeight: '1.5',
		color: THEME.colors.text.main,
		// Imported plain-text emails carry long unbroken URLs that would spill past the bubble.
		overflowWrap: 'anywhere' as const,
	},
	noteBadge: {
		...threadHeaderBadge,
		backgroundColor: THEME.colors.warning.main,
		color: THEME.colors.warning.contrastText,
	},
	forwardBadge: {
		...threadHeaderBadge,
		backgroundColor: THEME.colors.tertiary.main,
		color: THEME.colors.tertiary.contrastText,
	},
	deliveryFailedBadge: {
		...threadHeaderBadge,
		backgroundColor: THEME.colors.danger.main,
		color: THEME.colors.danger.contrastText,
	},
	loading: { padding: 24, color: THEME.colors.text.light },
	error: { padding: 24, color: THEME.colors.danger.dark },
	aiDraftBadge: {
		...threadHeaderBadge,
		backgroundColor: THEME.colors.secondary.main,
		color: THEME.colors.secondary.contrastText,
	},
	threadSystemClickable: {
		cursor: 'pointer',
		color: THEME.colors.secondary.main,
	},
	aiScoreBadgeInline: {
		marginLeft: 8,
		cursor: 'help',
	},
	threadBodyPreWrap: {
		whiteSpace: 'pre-wrap' as const,
	},
	showFullToggle: {
		display: 'inline-block',
		background: 'none',
		border: 'none',
		padding: '4px 0',
		marginTop: 8,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.primary.main,
		cursor: 'pointer',
		':hover': { textDecoration: 'underline' },
	},
	contextNoteToggle: {
		display: 'inline',
		height: 'auto',
		padding: 0,
		fontSize: THEME.typography.fontSizes.xs,
	},
	threadAttachments: {
		display: 'flex',
		flexWrap: 'wrap' as const,
		gap: 8,
		marginTop: 12,
		paddingTop: 10,
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	attachmentChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		padding: '6px 10px',
		backgroundColor: THEME.colors.background.main,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.main,
		textDecoration: 'none',
		cursor: 'pointer',
		':hover': {
			backgroundColor: THEME.colors.hover.light,
			borderColor: THEME.colors.primary.main,
		},
	},
	attachmentIcon: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
		flexShrink: 0,
	},
	attachmentPlayIcon: {
		color: THEME.colors.primary.main,
		flexShrink: 0,
	},
	attachmentName: {
		maxWidth: 180,
		overflow: 'hidden' as const,
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	attachmentSize: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
		flexShrink: 0,
	},
	attachmentDownloadAll: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		padding: '6px 10px',
		height: 'auto',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.primary.main,
	},
	attachmentDownloadAllIcon: {
		flexShrink: 0,
	},
	attachmentHoverPreview: {
		padding: THEME.spacing.xs,
		pointerEvents: 'none' as const,
	},
	attachmentHoverPreviewImage: {
		display: 'block',
		maxWidth: THEME.breakpoints.xs,
		maxHeight: THEME.breakpoints.xs / 2,
		borderRadius: THEME.common.borderRadius,
		objectFit: 'contain' as const,
	},
} as const;
